import { useCallback } from 'react';

import { ViewType } from '@this+that/models';
import log from 'loglevel';

import { resolveListingEmailAddresses } from './resolveListingEmailAddresses';
import { LABEL_PREFIXES } from '../../../components/InboxLabelsTabsFilter/constants';
import { FetchResult } from '../../../enums/fetchResult';
import { useGetPaginatedUserEmailCommunications } from '../../../services/userCommunications.service';
import {
  useIntegrationsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { hasActiveRemovedEmailAccounts } from '../../../store/Inbox/Integrations/removedEmailAccounts';

const getFetchGeneration = (): number =>
  useUserCommunicationsStore.getState().fetchGeneration;

const resolveStartKey = (viewTypes: ViewType[]): string | undefined => {
  const { startKeys } = useUserCommunicationsStore.getState();
  const singleViewType = viewTypes[0];

  if (viewTypes.length > 1) return startKeys.primary || undefined;

  if (singleViewType.startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
    const labelId = singleViewType.replace(LABEL_PREFIXES.GMAIL_LABEL, '');
    return startKeys.labels[labelId] || undefined;
  }

  const keyMap: Record<ViewType, string | null> = {
    PRIMARY: startKeys.primary,
    PROMOTIONS: startKeys.promotions,
    SOCIAL: startKeys.social,
    UPDATES: startKeys.updates,
    FORUMS: startKeys.forums,
    DRAFT: startKeys.draft,
    SENT: startKeys.sent,
    SPAM: startKeys.spam,
    TRASH: startKeys.trash,
    ALL_EMAILS: startKeys.all,
  } as const;

  if (keyMap[singleViewType] !== undefined) {
    return keyMap[singleViewType] || undefined;
  }
  return startKeys.labels[singleViewType] || undefined;
};

const isSameFilter = (a?: string[], b?: string[]): boolean => {
  if (a === undefined || b === undefined) return a === b;
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort();
  return [...a].sort().every((address, index) => address === sortedB[index]);
};

const isResponseStale = (
  requestGeneration: number,
  requestFilters: string[] | undefined,
  isScopedToAccountFilter: boolean,
): boolean =>
  requestGeneration !== getFetchGeneration() ||
  (isScopedToAccountFilter &&
    !isSameFilter(
      requestFilters,
      useIntegrationsStore.getState().currentEmailFilters,
    ));

interface FetchUserCommunicationParams {
  limit: number;
  isInitialFetch?: boolean;
  isScopedToAccountFilter?: boolean;
}

export const useFetchIndividualUserCommunications = ({
  limit,
  isInitialFetch = false,
  isScopedToAccountFilter = true,
}: FetchUserCommunicationParams) => {
  const { getPaginatedUserEmailCommunications } =
    useGetPaginatedUserEmailCommunications();

  const setLoading = useUserCommunicationsStore((state) => state.setLoading);
  const saveIndividualUserCommunications = useUserCommunicationsStore(
    (state) => state.saveIndividualUserCommunications,
  );
  const startKeys = useUserCommunicationsStore((state) => state.startKeys);
  const markViewTypesSynced = useUserCommunicationsStore(
    (state) => state.markViewTypesSynced,
  );

  const handleFetchIndividualUserCommunications = useCallback(
    async (
      viewType: ViewType | ViewType[],
      isLoadMore?: boolean,
      emailAddresses?: string[],
    ): Promise<FetchResult> => {
      const { integrationAccounts, removedEmailAccounts } =
        useIntegrationsStore.getState();
      const resolvedEmailAddresses = resolveListingEmailAddresses(
        emailAddresses,
        integrationAccounts,
        hasActiveRemovedEmailAccounts(removedEmailAccounts, Date.now()),
      );

      if (
        resolvedEmailAddresses !== undefined &&
        resolvedEmailAddresses.length === 0
      ) {
        const viewTypes = Array.isArray(viewType) ? viewType : [viewType];
        viewTypes.forEach((vt) =>
          saveIndividualUserCommunications([], null, vt, false),
        );
        if (!isLoadMore) {
          markViewTypesSynced(viewTypes);
        }
        return FetchResult.OK;
      }

      if (!isLoadMore) {
        setLoading(true);
      }

      const viewTypes = Array.isArray(viewType) ? viewType : [viewType];
      const requestGeneration = getFetchGeneration();

      try {
        const exclusiveStartKey =
          isInitialFetch || !isLoadMore
            ? undefined
            : resolveStartKey(viewTypes);

        const storeViewType = viewTypes[0];

        const response = await getPaginatedUserEmailCommunications({
          limit,
          viewTypes,
          exclusiveStartKey,
          emailAddresses: resolvedEmailAddresses,
        });

        if (
          isResponseStale(
            requestGeneration,
            emailAddresses,
            isScopedToAccountFilter,
          )
        ) {
          return FetchResult.STALE;
        }

        if (isLoadMore && resolveStartKey(viewTypes) !== exclusiveStartKey) {
          return FetchResult.STALE;
        }

        const { userCommunications, exclusiveStartKey: newExclusiveStartKey } =
          response;

        saveIndividualUserCommunications(
          userCommunications,
          newExclusiveStartKey,
          storeViewType,
          isLoadMore,
          viewTypes,
        );
        return FetchResult.OK;
      } catch (error) {
        log.error('Error fetching user communications:', error);
        return FetchResult.FAILED;
      } finally {
        if (!isLoadMore) {
          setLoading(false);
        }
        if (
          !isLoadMore &&
          !isResponseStale(
            requestGeneration,
            emailAddresses,
            isScopedToAccountFilter,
          )
        ) {
          markViewTypesSynced(viewTypes);
        }
      }
    },
    [
      getPaginatedUserEmailCommunications,
      isInitialFetch,
      isScopedToAccountFilter,
      limit,
      markViewTypesSynced,
      saveIndividualUserCommunications,
      setLoading,
    ],
  );

  const getHasMore = (viewType: ViewType): boolean => {
    if (viewType.startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
      const labelId = viewType.replace(LABEL_PREFIXES.GMAIL_LABEL, '');
      return Boolean(startKeys.labels[labelId]);
    }

    const keyMap: Record<
      Exclude<ViewType, `Label_${string}`>,
      string | null
    > = {
      PRIMARY: startKeys.primary,
      PROMOTIONS: startKeys.promotions,
      SOCIAL: startKeys.social,
      UPDATES: startKeys.updates,
      FORUMS: startKeys.forums,
      DRAFT: startKeys.draft,
      SENT: startKeys.sent,
      SPAM: startKeys.spam,
      TRASH: startKeys.trash,
      ALL_EMAILS: startKeys.all,
    };

    const fixedViewTypeKey =
      keyMap[viewType as Exclude<ViewType, `Label_${string}`>];
    if (fixedViewTypeKey !== undefined) {
      return Boolean(fixedViewTypeKey);
    }

    return Boolean(startKeys.labels[viewType]);
  };

  return {
    handleFetchIndividualUserCommunications,
    getHasMore,
  };
};
